import { cva } from "class-variance-authority";
import * as React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";

import { cn } from "../../../lib/utils";

import { type ColorShade, textColorShadeMapping } from "../../../theme";

const iconVariants = cva("inline-flex items-center justify-center", {
	variants: {
		color: textColorShadeMapping,
		size: {
			12: "size-3", // 12x12
			16: "size-4", // 16x16
			20: "size-5", // 20x20
			24: "size-6", // 24x24
			32: "size-8", // 32x32
			36: "size-9", // 36x36
			40: "size-10", // 40x40
			80: "size-20", // 80x80
		},
		disabled: {
			true: "opacity-40",
		},
	},
	defaultVariants: {
		color: "neutral-5",
		size: 16,
		disabled: false,
	},
});

export interface IconProps {
	/**
	 * The class name to apply to the Icon
	 */
	dangerouslySetClassName?: string;
	/**
	 * The color of the Icon
	 */
	color?: null | ColorShade;
	/**
	 * The size of the Icon in pixels
	 */
	size?: 12 | 16 | 20 | 24 | 32 | 36 | 40 | 80;
	/**
	 * Whether the icon is disabled
	 */
	disabled?: boolean;
	/**
	 * The FontAwesome icon definition
	 */
	icon: IconDefinition;
}

/**
 * An Icon component that standardizes the display of FontAwesome icons
 */
const Icon = React.forwardRef<HTMLDivElement, IconProps>(function Icon(
	props: IconProps,
	ref: React.Ref<HTMLDivElement>
) {
	const { color = "neutral-5", size = 16, dangerouslySetClassName, icon, disabled = false } = props;

	// Map the size prop to a specific icon size (scaled down to leave padding)
	const iconSize = {
		12: "9px",
		16: "12px",
		20: "15px",
		24: "18px",
		32: "24px",
		36: "32px",
		40: "36px",
		80: "60px",
	}[size];

	return (
		<div
			className={cn(iconVariants({ color, size, disabled }), dangerouslySetClassName)}
			ref={ref}
			data-testid={`icon-wrapper-${icon.iconName}`}
		>
			<FontAwesomeIcon icon={icon} style={{ fontSize: iconSize }} />
		</div>
	);
});

Icon.displayName = "Icon";

export { Icon, iconVariants };
